// CostManager.js
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";
import { addCost, deleteCost, editCost } from "helpers/fakebackend_helper";
import React, { useState } from "react";
import {
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    InputGroup,
    InputGroupText,
} from "reactstrap";

const costTypes = [
    "Parking Lot Cost",
    "Partner Remittance Fee",
    "Other Costs",
];

const costTypeToLocationKey = {
    "Parking Lot Cost": "parking_lot_cost",
    "Partner Remittance Fee": "partner_remittance_fee",
};

const CostManager = ({ location_id, location, costs, handleSave }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCost, setEditingCost] = useState(null);
    const [formData, setFormData] = useState({
        type: "Parking Lot Cost",
        payment_date: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
        amount: location.parking_lot_cost,
        note: "",
    });

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleAddClick = () => {
        setEditingCost(null);
        setFormData({
            type: "Parking Lot Cost",
            payment_date: new Date().toISOString().slice(0, 10),
            amount: location.parking_lot_cost,
            note: "",
        });
        toggleModal();
    };

    const handleEditClick = (cost) => {
        setEditingCost(cost);
        setFormData({
            type: cost.type,
            payment_date: cost.payment_date,
            amount: cost.amount,
            note: cost.note || "",
        });
        toggleModal();
    };

    const handleDeleteClick = async (id) => {
        await deleteCost(user.token, id);
        handleSave();
    };

    const onSave = async () => {
        if (editingCost) {
            await editCost(user.token, editingCost.id, formData);
        } else {
            const newCost = {
                location_id,
                ...formData,
            };
            await addCost(user.token, newCost);
        }
        toggleModal();
        handleSave();
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 style={{ color: "#FFCA5C", fontWeight: "bold" }}>Costs/Payables</h5>
                <Button className="button-gradient-bg mb-3" onClick={handleAddClick}>
                    Add Cost / Payable
                </Button>
            </div>

            <Table className="align-middle table-hover table-dark table-nowrap mb-0">
                <thead className="table-danger">
                    <tr>
                        <th>Type</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Note</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {costs && costs.length > 0 ? (
                        costs.map((cost) => (
                            <tr key={cost.id}>
                                <td>{cost.type}</td>
                                <td>{formatDate(cost.payment_date)}</td>
                                <td>${formatCurrency(cost.amount)}</td>
                                <td>{cost.note || ""}</td>
                                <td>
                                    <Button
                                        color="link"
                                        className="p-0 me-2"
                                        onClick={() => handleEditClick(cost)}
                                    >
                                        <i className="bx bx-pencil"></i>
                                    </Button>
                                    <Button
                                        color="link"
                                        className="p-0 text-danger"
                                        onClick={() => handleDeleteClick(cost.id)}
                                    >
                                        <i className="bx bx-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={5} className="text-center text-muted">
                                No costs recorded
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Add/Edit Modal */}
            <Modal isOpen={isModalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {editingCost ? "Edit Cost" : "Add Cost"}
                </ModalHeader>
                <ModalBody>
                    <label>Type</label>
                    <Input
                        type="select"
                        value={formData.type}
                        onChange={(e) => {
                            const newType = e.target.value;
                            const key = costTypeToLocationKey[newType];
                            const newAmount = key && location ? location[key] || 0 : formData.amount;

                            setFormData({
                                ...formData,
                                type: newType,
                                amount: newAmount,
                            });
                        }}
                    >
                        {costTypes.map((ct) => (
                            <option key={ct} value={ct}>
                                {ct}
                            </option>
                        ))}
                    </Input>

                    <label className="mt-3">Date</label>
                    <Input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) =>
                            setFormData({ ...formData, payment_date: e.target.value })
                        }
                    />

                    <label className="mt-3">Amount</label>
                    <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input
                            type="number"
                            value={formData.amount}
                            onChange={(e) =>
                                setFormData({ ...formData, amount: e.target.value })
                            }
                        />
                    </InputGroup>

                    <label className="mt-3">Note (optional)</label>
                    <Input
                        type="text"
                        value={formData.note}
                        onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                        placeholder="Optional note"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button className="button-gradient-bg" onClick={onSave}>
                        Save
                    </Button>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CostManager;